/**
 * Haptics — one small home for every buzz in the app. Rather than scatter raw
 * navigator.vibrate calls about, the whole interface speaks in a few named,
 * intent-first LEVELS and this maps each to a vibration pattern, so the feel is
 * consistent and tunable in one place. On a boat the point is physical: a cold,
 * wet, gloved hand should be able to feel that a tap registered without looking
 * at the screen, and a consequential action should feel clearly firmer than an
 * everyday one so there's no mistaking the two.
 *
 * The whole app should speak ONLY in these levels so the vocabulary stays small
 * and the feel stays predictable:
 *   - tick    a very light buzz, for ADJUSTING A VALUE (a stepper +/−, a slider
 *             notch, a chip). It's the lightest of the lot so nudging a number a
 *             dozen times never feels buzzy.
 *   - select  a light buzz, for a NORMAL TAP that does something (a toggle, a
 *             favourite, opening a sheet, a primary button). A touch more
 *             presence than a tick, so a tap feels different from a value nudge.
 *   - commit  a firmer, notification-style buzz for ARMING or committing a
 *             SAFETY or otherwise consequential action (drop the anchor, arm the
 *             watch, follow a route, confirm a delete). Deliberately distinct
 *             from a normal tap so an important action feels important.
 *   - alarm   the notification pattern a RAISED safety alarm buzzes (anchor
 *             drag, grounding ahead, close CPA…). This IS the warn/alert level;
 *             it's fired only on the alarm rising edge (see HapticsRunner), and
 *             reused here rather than duplicated.
 *   - mob     the strongest, unmistakable pattern, reserved for man overboard.
 *
 * Capacitor mapping (for when this moves off the Web Vibration API onto
 * @capacitor/haptics): tick → ImpactStyle.Light, select → ImpactStyle.Medium,
 * commit → ImpactStyle.Heavy / NotificationType.Success, alarm →
 * NotificationType.Warning, mob → NotificationType.Error. The escalation in the
 * durations below mirrors that light → medium → heavy → notification climb.
 *
 * Every level is fire-and-forget: haptic() never awaits, never blocks a handler,
 * and never throws. It degrades cleanly where the Web Vibration API isn't
 * supported (most desktop browsers, iOS Safari, and the test environment) —
 * there's just no buzz. It's also gated by the user's Haptics toggle (App &
 * Account → Haptics, default on); the settings store calls setHapticsEnabled
 * when that flips, so this module never has to know about the store.
 */

export type HapticIntent = 'tick' | 'select' | 'commit' | 'alarm' | 'mob';

/**
 * The vibration pattern per level, in milliseconds. A single number is one buzz;
 * an array alternates buzz/pause/buzz… (the Web Vibration API convention). The
 * totals climb tick < select < commit < alarm < mob, so the feel escalates with
 * the weight of the action. commit is a short firm double so it reads as one
 * deliberate "ba-dum" against a plain tap, never a stream.
 */
export const HAPTIC_PATTERNS: Record<HapticIntent, number | number[]> = {
  tick: 10,
  select: 18,
  commit: [28, 40, 28],
  alarm: [90, 60, 90],
  mob: [220, 120, 220, 120, 220],
};

/** Whether haptics are on. Default on; the settings store flips this. */
let enabled = true;

type VibrateFn = (pattern: number | number[]) => boolean;

/** Resolve the platform vibrate function, or null when unsupported. */
function resolveVibrate(): VibrateFn | null {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    return (pattern) => navigator.vibrate(pattern);
  }
  return null;
}

let vibrateImpl: VibrateFn | null = resolveVibrate();

/** Turn haptics on or off (driven by the user's Haptics toggle). */
export function setHapticsEnabled(on: boolean): void {
  enabled = on;
}

/** Whether haptics are currently enabled. */
export function isHapticsEnabled(): boolean {
  return enabled;
}

/** The vibration pattern for an intent (exposed for testing and tuning). */
export function hapticPattern(intent: HapticIntent): number | number[] {
  return HAPTIC_PATTERNS[intent];
}

/**
 * Fire the haptic for an intent. Does nothing (and never throws) when haptics
 * are toggled off or the platform has no Vibration API. Returns true only when a
 * buzz was actually requested, so callers/tests can tell.
 */
export function haptic(intent: HapticIntent): boolean {
  if (!enabled) return false;
  const fn = vibrateImpl;
  if (!fn) return false;
  try {
    return fn(HAPTIC_PATTERNS[intent]) !== false;
  } catch {
    // A SecurityError or a stubborn platform: swallow it, the UI carries on.
    return false;
  }
}

/**
 * Test seam: replace the vibrate sink (pass null to simulate an unsupported
 * platform). Not used in app code.
 */
export function __setVibrateForTest(fn: VibrateFn | null): void {
  vibrateImpl = fn;
}

/** Test seam: restore the real platform vibrate resolution. */
export function __resetVibrateForTest(): void {
  vibrateImpl = resolveVibrate();
  enabled = true;
}
